import { useEffect, useState, useRef, useCallback, Suspense, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import Map, { Marker } from 'react-map-gl/maplibre'
import type { MapRef } from 'react-map-gl/maplibre'
import { Home } from 'lucide-react'
import { toast } from 'sonner'
import 'maplibre-gl/dist/maplibre-gl.css'
import { useTranslation } from 'react-i18next'

import { getApartmentsInBounds, getApartmentsByIds } from '@services/livability/apartments'
import { useApartmentFilterOptions } from '@services/livability/queries/useApartmentFilterOptions'
import {
  apartmentAnalyticsProps,
  trackEvent
} from '@services/livability/analytics'
import type { Apartment } from '@services/livability/types'

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '@/components/ui/card'
import { formatPrice } from '@/lib/utils'

import { ApartmentDetailsPanel } from '../components/ApartmentDetailsPanel'
import { ListingModeToggle } from '../components/ListingModeToggle'
import { IndexCategorySelect } from '../components/IndexCategorySelect'
import { FilterBar, FilterControl } from '../components/filters/FilterBar'
import { MoreFiltersDialog } from '../components/filters/MoreFiltersDialog'
import { useApartmentFilters } from '../hooks/useApartmentFilters'
import {
  buildFiltersAppliedEventProps,
  filtersToApiParams,
  type ApartmentFilters
} from '../utils/apartmentFilterUrl'
import { useLivabilityPaths } from '../utils/paths'
import { SLOVAK_CITY_CENTERS } from '../utils/slovakCityCenters'

const popupStyles = `
  .maplibregl-popup-content {
    border-radius: 0.75rem;
    padding: 0;
    box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  }
`

const KOSICE_CENTER = {
  longitude: 21.2611,
  latitude: 48.7164,
  zoom: 13
}

const CITY_FLY_ZOOM = 12.5

function getMarkerColors(index?: number | null): {
  bg: string
  text: string
  shadow: string
} {
  if (!index) {
    return {
      bg: '#6b7280',
      text: '#ffffff',
      shadow: '0 4px 6px -1px rgba(107, 114, 128, 0.3)'
    }
  }

  const score = Math.max(0, Math.min(100, index))

  let r: number, g: number, b: number

  if (score < 50) {
    const t = score / 50
    r = Math.round(220 + (234 - 220) * t)
    g = Math.round(38 + (179 - 38) * t)
    b = Math.round(38 + (8 - 38) * t)
  } else {
    const t = (score - 50) / 50
    r = Math.round(234 + (34 - 234) * t)
    g = Math.round(179 + (197 - 179) * t)
    b = Math.round(8 + (94 - 8) * t)
  }

  const bgColor = `rgb(${r}, ${g}, ${b})`
  const shadowColor = `rgba(${r}, ${g}, ${b}, 0.4)`

  return {
    bg: bgColor,
    text: '#ffffff',
    shadow: `0 4px 6px -1px ${shadowColor}`
  }
}

function ApartmentsMapContent() {
  const { t } = useTranslation('livability')
  const [searchParams] = useSearchParams()
  const paths = useLivabilityPaths()
  const mapRef = useRef<MapRef>(null)
  const byIdsAbortRef = useRef<AbortController | null>(null)
  const inViewAbortRef = useRef<AbortController | null>(null)
  const [apartments, setApartments] = useState<Apartment[]>([])
  const [selectedApartment, setSelectedApartment] = useState<Apartment | null>(
    null
  )
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const {
    filters,
    listingType,
    indexCategory,
    setFilters,
    clearFilters
  } = useApartmentFilters()

  const mapApiParams = useMemo(() => filtersToApiParams(filters), [filters])
  const { data: filterOptions } = useApartmentFilterOptions(listingType)

  const handleFiltersChange = useCallback(
    (nextFilters: ApartmentFilters) => {
      const nextCity = nextFilters.city
      if (nextCity && nextCity !== filters.city) {
        const slug = nextCity
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[\s_]+/g, '-')
        const center = SLOVAK_CITY_CENTERS[slug]
        if (center) {
          mapRef.current?.flyTo({
            center,
            zoom: CITY_FLY_ZOOM,
            duration: 1200
          })
        }
      }
      setFilters(nextFilters, { resetPage: false })
      trackEvent(
        'filters_applied',
        buildFiltersAppliedEventProps('map', listingType, nextFilters)
      )
    },
    [filters.city, listingType, setFilters]
  )

  const handleClearFilters = useCallback(() => {
    clearFilters()
    trackEvent(
      'filters_applied',
      buildFiltersAppliedEventProps('map', listingType, filters, { cleared: true })
    )
  }, [clearFilters, filters, listingType])

  const idsParam = searchParams.get('ids')
  const fromChat = searchParams.get('from') === 'chat'
  const apartmentIds = useMemo(
    () =>
      idsParam
        ? idsParam
            .split(',')
            .map(Number)
            .filter((id) => !isNaN(id))
        : null,
    [idsParam]
  )
  const [hasLoadedByIds, setHasLoadedByIds] = useState(false)
  const [totalRequestedIds, setTotalRequestedIds] = useState<number>(0)

  const [viewState, setViewState] = useState({
    longitude: KOSICE_CENTER.longitude,
    latitude: KOSICE_CENTER.latitude,
    zoom: KOSICE_CENTER.zoom
  })

  const fetchApartmentsByIds = useCallback(async () => {
    if (!apartmentIds || apartmentIds.length === 0 || hasLoadedByIds) return

    byIdsAbortRef.current?.abort()
    const controller = new AbortController()
    byIdsAbortRef.current = controller

    setIsLoading(true)
    setTotalRequestedIds(apartmentIds.length)
    try {
      const data = await getApartmentsByIds(
        apartmentIds,
        indexCategory,
        controller.signal
      )
      setApartments(data)
      setHasLoadedByIds(true)
      setError(null)

      const apartmentsWithCoords = data.filter(
        (apt) => apt.coordinates && apt.coordinates.length === 2
      )

      if (apartmentsWithCoords.length < data.length) {
        toast.warning(
          t('map.missingCoords', {
            shown: apartmentsWithCoords.length,
            total: apartmentIds.length
          })
        )
      }

      if (apartmentsWithCoords.length > 0 && mapRef.current) {
        const lngs = apartmentsWithCoords.map((apt) => apt.coordinates![0])
        const lats = apartmentsWithCoords.map((apt) => apt.coordinates![1])

        const minLng = Math.min(...lngs)
        const maxLng = Math.max(...lngs)
        const minLat = Math.min(...lats)
        const maxLat = Math.max(...lats)

        const lngPadding = (maxLng - minLng) * 0.1 || 0.01
        const latPadding = (maxLat - minLat) * 0.1 || 0.01

        mapRef.current.fitBounds(
          [
            [minLng - lngPadding, minLat - latPadding],
            [maxLng + lngPadding, maxLat + latPadding]
          ],
          { padding: 50, duration: 1000 }
        )
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return
      setError(
        err instanceof Error ? err.message : 'Failed to load apartments'
      )
    } finally {
      if (!controller.signal.aborted) setIsLoading(false)
    }
  }, [apartmentIds, hasLoadedByIds, indexCategory])

  const fetchApartmentsInView = useCallback(async () => {
    if (apartmentIds && apartmentIds.length > 0) return
    if (!mapRef.current) return

    inViewAbortRef.current?.abort()
    const controller = new AbortController()
    inViewAbortRef.current = controller

    setIsLoading(true)
    try {
      const map = mapRef.current.getMap()
      const bounds = map.getBounds()

      const data = await getApartmentsInBounds({
        minLng: bounds.getWest(),
        minLat: bounds.getSouth(),
        maxLng: bounds.getEast(),
        maxLat: bounds.getNorth(),
        indexCategory,
        filters: mapApiParams,
        listingType,
        signal: controller.signal
      })
      setApartments(data)
      setError(null)
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return
      setError(
        err instanceof Error ? err.message : 'Failed to load apartments'
      )
    } finally {
      if (!controller.signal.aborted) setIsLoading(false)
    }
  }, [apartmentIds, indexCategory, listingType, mapApiParams])

  useEffect(() => {
    if (apartmentIds && apartmentIds.length > 0) {
      fetchApartmentsByIds()
    } else {
      fetchApartmentsInView()
    }
  }, [
    fetchApartmentsInView,
    fetchApartmentsByIds,
    apartmentIds,
    listingType,
    indexCategory,
    mapApiParams
  ])

  const onMoveEnd = useCallback(() => {
    if (!apartmentIds || apartmentIds.length === 0) {
      fetchApartmentsInView()
    }
  }, [fetchApartmentsInView, apartmentIds])

  useEffect(() => {
    return () => {
      byIdsAbortRef.current?.abort()
      inViewAbortRef.current?.abort()
    }
  }, [])

  if (error && !apartments.length) {
    return (
      <div className="relative flex h-full min-h-0 w-full items-center justify-center overflow-hidden">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-destructive">{t('map.error')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p>{error}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="app-chrome relative min-h-0 flex-1 w-full overflow-hidden">
      <style>{popupStyles}</style>
      <div className="pointer-events-none absolute inset-x-0 top-0 z-10">
        <div className="pointer-events-auto mt-3 px-4">
          <FilterBar
            filters={filters}
            filterOptions={filterOptions}
            onChange={handleFiltersChange}
            onClearAll={handleClearFilters}
            className="mx-auto w-fit max-w-full"
            leading={
              <FilterControl label="Ponuka">
                <ListingModeToggle compact />
              </FilterControl>
            }
            trailing={
              <p className="hidden whitespace-nowrap text-[13px] text-muted-foreground xl:block">
                {fromChat && totalRequestedIds > 0
                  ? t('map.showingOfTotal', {
                      shown: apartments.filter((a) => a.coordinates).length,
                      total: totalRequestedIds
                    })
                  : t('map.inView', { count: apartments.length })}
              </p>
            }
          >
            <FilterControl label="Hodnotenie">
              <IndexCategorySelect
                value={indexCategory}
                onChange={(value) =>
                  handleFiltersChange({ ...filters, indexCategory: value })
                }
                hidePrefix
                className="h-7 w-auto gap-1.5 rounded-[10px] border-0 bg-ink/[0.04] px-2.5 text-[13px] font-semibold shadow-none"
              />
            </FilterControl>
            <MoreFiltersDialog
              filters={filters}
              filterOptions={filterOptions}
              listingType={listingType}
              onChange={handleFiltersChange}
            />
          </FilterBar>
        </div>
      </div>

      <Map
        ref={mapRef}
        {...viewState}
        onMove={(evt) => setViewState(evt.viewState)}
        onMoveEnd={onMoveEnd}
        onLoad={fetchApartmentsInView}
        style={{ position: 'absolute', inset: 0, zIndex: 0 }}
        mapStyle="https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json"
      >
        {apartments.map((apartment) => {
          const coords = apartment.coordinates

          if (!coords || coords.length !== 2) {
            return null
          }

          const [lng, lat] = coords
          const indexScore = apartment.index?.index
          const colors = getMarkerColors(indexScore)

          return (
            <Marker
              key={apartment.id}
              longitude={lng}
              latitude={lat}
              anchor="bottom"
              onClick={(e) => {
                e.originalEvent.stopPropagation()
                setSelectedApartment(apartment)
                trackEvent(
                  'apartment_viewed',
                  apartmentAnalyticsProps(apartment, 'map', indexCategory)
                )
              }}
            >
              <div className="cursor-pointer transform transition-transform hover:scale-110">
                <div
                  className="rounded-lg px-3 py-1.5 flex items-center gap-1.5 font-medium"
                  style={{
                    backgroundColor: colors.bg,
                    color: colors.text,
                    boxShadow: colors.shadow
                  }}
                >
                  <Home className="h-3.5 w-3.5" />
                  <span className="text-sm whitespace-nowrap">
                    {formatPrice(
                      apartment.price_month,
                      listingType === 'rent' ? '/mes.' : ''
                    )}
                  </span>
                </div>
              </div>
            </Marker>
          )
        })}
      </Map>

      {selectedApartment && (
        <ApartmentDetailsPanel
          apartment={selectedApartment}
          onClose={() => setSelectedApartment(null)}
          indexCategory={indexCategory}
          detailPath={`${paths.detail(selectedApartment.id)}`}
        />
      )}

      {isLoading && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
          <Card className="px-4 py-2">
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              <span className="text-sm">{t('map.loading')}</span>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}

export function ApartmentsMap() {
  const { t } = useTranslation('livability')
  return (
    <Suspense
      fallback={
        <div className="relative flex h-full min-h-0 w-full items-center justify-center overflow-hidden">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">{t('map.loadingMap')}</p>
          </div>
        </div>
      }
    >
      <ApartmentsMapContent />
    </Suspense>
  )
}
